import { useEffect, useRef, useState } from "react";
import { Check, MessageCircle, Send, X } from "lucide-react";

const IG_DM_URL = "https://ig.me/m/splurgefinance";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open && !sent) textareaRef.current?.focus();
  }, [open, sent]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSend = async () => {
    const text = message.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // the drafted message stays visible in the confirmation so it can be copied manually
    }
    window.open(IG_DM_URL, "_blank", "noopener,noreferrer");
    setSent(true);
  };

  const reset = () => {
    setMessage("");
    setSent(false);
  };

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      {open && (
        <div
          data-testid="chat-panel"
          role="dialog"
          aria-label="Get in touch with Splurge on Instagram"
          className="chat-pop w-[calc(100vw-2.5rem)] max-w-sm overflow-hidden rounded-3xl border border-splurge-lavender bg-white shadow-2xl shadow-splurge-dark/25"
        >
          <div className="flex items-center gap-3 bg-gradient-to-r from-splurge-dark to-splurge-primary px-5 py-4">
            <img
              src="/logo.png"
              alt=""
              aria-hidden="true"
              className="h-10 w-10 rounded-full ring-2 ring-white/30"
            />
            <div className="flex-1">
              <p className="text-sm font-extrabold text-white">Get in touch</p>
              <p className="text-xs font-semibold text-white/70">@splurgefinance on Instagram</p>
            </div>
            <button
              type="button"
              data-testid="chat-close-button"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              className="grid h-8 w-8 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {sent ? (
            <div className="p-5 text-center">
              <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-splurge-lavender">
                <Check className="h-6 w-6 text-splurge-primary" aria-hidden="true" />
              </span>
              <p className="mt-3 text-base font-extrabold text-splurge-ink" data-testid="chat-sent-confirmation">
                Message copied!
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-splurge-ink/60">
                The Instagram chat with @splurgefinance just opened. Paste your message there
                and hit send.
              </p>
              <div className="mt-4 rounded-2xl bg-splurge-bg p-3.5 text-left text-sm leading-relaxed text-splurge-ink/70">
                &ldquo;{message}&rdquo;
              </div>
              <div className="mt-4 flex gap-2.5">
                <a
                  href={IG_DM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="chat-open-instagram"
                  aria-label="Open Instagram DM chat with Splurge Finance (opens in a new tab)"
                  className="flex-1 rounded-full bg-splurge-primary px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-splurge-dark"
                >
                  Open Instagram again
                </a>
                <button
                  type="button"
                  data-testid="chat-write-another"
                  onClick={reset}
                  className="flex-1 rounded-full border border-splurge-ink/12 px-4 py-2.5 text-xs font-bold text-splurge-ink transition-colors hover:border-splurge-primary/40 hover:text-splurge-primary"
                >
                  Write another
                </button>
              </div>
            </div>
          ) : (
            <div className="p-5">
              <p className="text-sm leading-relaxed text-splurge-ink/60">
                Draft your enquiry here. When you press send, we copy it and open our Instagram
                DMs, so you can paste it straight in.
              </p>
              <textarea
                ref={textareaRef}
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="chat-message-input"
                aria-label="Your message to Splurge"
                placeholder="Hi Splurge! I wanted to ask about..."
                className="mt-4 w-full resize-none rounded-2xl border border-splurge-lavender bg-splurge-bg px-4 py-3 text-sm text-splurge-ink outline-none transition-colors placeholder:text-splurge-ink/35 focus:border-splurge-primary"
              />
              <button
                type="button"
                data-testid="chat-send-button"
                onClick={handleSend}
                disabled={!message.trim()}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-splurge-primary px-5 py-3 text-sm font-bold text-white shadow-lg shadow-splurge-primary/30 transition-all duration-200 hover:bg-splurge-dark active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
              >
                Send via Instagram
                <Send className="h-4 w-4" aria-hidden="true" />
              </button>
              <p className="mt-2.5 text-center text-[11px] font-medium text-splurge-ink/40">
                Opens Instagram in a new tab
              </p>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        data-testid="chat-open-button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close chat" : "Get in touch via Instagram DM"}
        className="grid h-14 w-14 place-items-center rounded-full bg-splurge-primary text-white shadow-xl shadow-splurge-primary/40 transition-all duration-200 hover:scale-105 hover:bg-splurge-dark active:scale-95"
      >
        {open ? (
          <X className="h-6 w-6" aria-hidden="true" />
        ) : (
          <MessageCircle className="h-6 w-6" aria-hidden="true" />
        )}
      </button>
    </div>
  );
};
